import React from 'react';
import { Table, Thead, Tbody, Tr, Td, Center } from '@chakra-ui/react';

interface Column<T = any> {
  header: string;
  key: keyof T | string;
  render?: (value: any, item: T) => React.ReactNode;
  width?: string | number;
}

interface DataTableProps<T = any> {
  data: T[] | Record<string, any>;
  columns?: Column<T>[];
  keyColumn?: string;
  valueColumn?: string;
  emptyMessage?: string;
  variant?: string;
}

export const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  keyColumn = 'key',
  valueColumn = 'value',
  emptyMessage = 'N/A',
  variant = 'simple',
}: DataTableProps<T>) => {
  // Handle empty data
  if (
    !data ||
    (Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0)
  ) {
    return <Center>{emptyMessage}</Center>;
  }

  // Handle object data (key-value pairs) - backward compatibility
  if (!Array.isArray(data) && !columns) {
    const entries = Object.entries(data);
    return (
      <Table variant={variant}>
        <Thead>
          <Tr>
            <Td>{keyColumn}</Td>
            <Td>{valueColumn}</Td>
          </Tr>
        </Thead>
        <Tbody>
          {entries.map(([key, value]) => (
            <Tr key={key}>
              <Td>{key}</Td>
              <Td>{Array.isArray(value) ? value.join(', ') : String(value)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }

  // Handle array data with columns
  if (Array.isArray(data) && columns) {
    return (
      <Table variant={variant}>
        <Thead>
          <Tr>
            {columns.map((column, index) => (
              <Td key={index} minW={column.width}>
                {column.header}
              </Td>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((item, index) => (
            <Tr key={index}>
              {columns.map((column, colIndex) => (
                <Td key={colIndex} minW={column.width}>
                  {column.render
                    ? column.render(item[column.key], item)
                    : String(item[column.key] ?? '')}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }

  // Fallback for array data without columns
  if (Array.isArray(data)) {
    return (
      <Table variant={variant}>
        <Tbody>
          {data.map((item, index) => (
            <Tr key={index}>
              <Td>{String(item)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }

  return <Center>{emptyMessage}</Center>;
};

// Convenience components for backward compatibility
export const VocTable: React.FC<{
  key_name?: string;
  value_name?: string;
  data: Record<string, string[]>;
}> = ({ key_name = '字', value_name = '拼音', data }) => (
  <DataTable data={data} keyColumn={key_name} valueColumn={value_name} />
);

export const VocabularyTable: React.FC<{
  data: Record<string, number>;
}> = ({ data }) => (
  <DataTable data={data} keyColumn="年级" valueColumn="用字量" />
);

export const MustWordsTable: React.FC<{
  data: Record<string, number>;
}> = ({ data }) => (
  <DataTable data={data} keyColumn="指定字" valueColumn="出现频率" />
);
